import { Calendar, CheckCircle2, AlertTriangle, XCircle, FileText, ArrowRight } from 'lucide-react';

interface Inspection {
    id: string;
    inspectionType: string;
    inspectionDate: string;
    nextDueDate: string;
    status: 'PASSED' | 'FAILED' | 'ACTION_REQUIRED';
    notes?: string;
    equipmentNumber: string;
    equipmentType: string;
    facilityName: string;
}

interface InspectionCardProps {
    inspection: Inspection;
}

export default function InspectionCard({ inspection }: InspectionCardProps) {
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'PASSED': return 'text-safety-400 border-safety-500/30 bg-safety-500/10';
            case 'ACTION_REQUIRED': return 'text-amber-400 border-amber-500/30 bg-amber-500/10';
            case 'FAILED': return 'text-alert-400 border-alert-500/30 bg-alert-500/10';
            default: return 'text-steel-400 border-steel-500/30 bg-steel-500/10';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'PASSED': return <CheckCircle2 className="w-5 h-5" />;
            case 'ACTION_REQUIRED': return <AlertTriangle className="w-5 h-5" />;
            case 'FAILED': return <XCircle className="w-5 h-5" />;
            default: return <FileText className="w-5 h-5" />;
        }
    };

    return (
        <div className="card-glass p-6 group hover:border-ember-500/30 transition-all duration-300">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-1 rounded text-xs font-bold border ${getStatusColor(inspection.status)} flex items-center gap-1`}>
                            {getStatusIcon(inspection.status)}
                            {inspection.status.replace('_', ' ')}
                        </span>
                        <span className="text-steel-500 text-sm">{inspection.inspectionType}</span>
                    </div>
                    <h3 className="font-bold text-white text-lg">{inspection.equipmentNumber}</h3>
                    <p className="text-steel-400 text-sm">{inspection.equipmentType} • {inspection.facilityName}</p>
                </div>

                <div className="text-right">
                    <div className="text-steel-500 text-sm mb-1">Performed on</div>
                    <div className="text-white font-mono">
                        {new Date(inspection.inspectionDate).toLocaleDateString()}
                    </div>
                </div>
            </div>

            {inspection.notes && (
                <div className="bg-charcoal-900/50 p-3 rounded-lg mb-4 text-sm text-steel-300 italic border border-steel-800">
                    "{inspection.notes}"
                </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-steel-800">
                <div className="flex items-center gap-2 text-sm text-steel-400">
                    <Calendar className="w-4 h-4 text-ember-500" />
                    Next Due: <span className="text-white">{new Date(inspection.nextDueDate).toLocaleDateString()}</span>
                </div>

                <button className="text-ember-400 hover:text-ember-300 text-sm font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    View Details <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
